<template>
  <div class="results">
    <h2>Search results</h2>

    <FilterButtons
      :filters="SearchFeedTypes"
      :active-filter="activeFilter"
      :disabled="isFiltersDisabled"
      class="mt-4"
      @filter-change="handleFilterClick"
    />

    <b-overlay :show="loading" rounded="sm" variant="transparent">
      <p v-if="!loading && isNothingFound" class="mt-5">No results found</p>

      <div
        v-else
        v-masonry
        class="results__carousel mt-4"
        transition-duration="1s"
        fit-width="true"
        gutter="15"
        column-width=".results__item"
        item-selector=".results__item"
        horizontal-order="true"
      >
        <div
          v-for="(item, index) in itemsToShow"
          :key="`${index + item._id}`"
          v-masonry-tile
          class="results__item pb-2 cursor-pointer"
          @click="handleItemClick(item)"
        >
          <img
            v-if="isProductDigitalOnly(item)"
            class="results__item-digital-badge"
            src="@/assets/icons/pdf-badge.svg"
            alt="digital-format"
          />
          <CloudinaryImage
            :id="item._id"
            :url="getImage(item)"
            :alt="`results-type-${item._id}`"
            :modifiers="{
              gravity: 'face',
              roundCorner: item.entity === SearchFeedTypes.PRODUCTS ? '0' : '10'
            }"
            width="160px"
            height="160px"
          />
          <p class="mb-2 mt-2 results__item-title">{{ getTitle(item) }}</p>
          <p>{{ getHandle(item) }}</p>
        </div>
      </div>
    </b-overlay>

    <div class="text-center pb-5 pt-3">
      <Button
        v-if="isViewMoreVisible"
        :loading="viewMoreLoading"
        variant="outline"
        size="sm"
        @click="handleShowMoreClick"
      >
        View more
      </Button>
    </div>
  </div>
</template>

<script>
import { take, isEmpty } from 'lodash';
import CloudinaryImage from '@/components/Elements/Image';
import { FallbackImageURL, ImageTypes } from '@/misc/constants';
import Button from '@/components/Elements/Button';
import FilterButtons from '@/components/Elements/FilterButtons';
import isItemHasOnlyDigitalFormat from '@/misc/isItemHasOnlyDigitalFormat';
import { SearchFeedTypes } from '@/components/PreviewItems/constants';

export default {
  name: 'SearchBlock',

  components: {
    Button,
    FilterButtons,
    CloudinaryImage
  },

  props: {
    items: {
      type: Array,
      default: () => []
    },
    countShowedItems: {
      type: Number,
      default: 10
    },
    viewMoreLoading: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    totals: {
      type: Object,
      default: () => {
        return {
          creators: 0,
          products: 0,
          recipes: 0
        };
      }
    }
  },

  data() {
    return {
      activeFilter: SearchFeedTypes.ALL,
      SearchFeedTypes
    };
  },

  computed: {
    filteredItems() {
      return this.activeFilter === SearchFeedTypes.ALL
        ? this.items
        : this.items.filter((item) => item.entity === this.activeFilter);
    },
    itemsToShow() {
      return take(this.filteredItems, this.countShowedItems);
    },
    isViewMoreVisible() {
      if (this.activeFilter === SearchFeedTypes.ALL) {
        return this.countShowedItems < this.totalResultsCount && !this.loading;
      } else {
        return (
          this.countShowedItems < this.totals[`${this.activeFilter}s`] &&
          !this.loading
        );
      }
    },
    totalResultsCount() {
      return Object.values(this.totals).reduce(
        (total, item) => total + item,
        0
      );
    },
    isNothingFound() {
      return isEmpty(this.filteredItems);
    },
    isFiltersDisabled() {
      return isEmpty(this.items);
    }
  },

  methods: {
    getImage(item) {
      switch (item.entity) {
        case SearchFeedTypes.CREATORS: {
          const creatorPhotos = this.$get(item, 'profile.photos', []);
          const photo = creatorPhotos.find(
            (image) => image.role === ImageTypes.Main
          );
          return this.$get(photo, 'imageUrl', FallbackImageURL);
        }
        case SearchFeedTypes.RECIPES:
          return (
            this.$get(item, 'images[0].fileURL', '') ||
            this.$get(item, 'images[0].url', FallbackImageURL)
          );
        case SearchFeedTypes.PRODUCTS:
          return this.$get(item, 'previewImages[0].url', FallbackImageURL);
        default:
          return FallbackImageURL;
      }
    },
    getTitle(item) {
      switch (item.entity) {
        case SearchFeedTypes.CREATORS:
          return '@' + this.$get(item, 'profile.handle', '');
        case SearchFeedTypes.RECIPES:
          return this.$get(item, 'name', '');
        case SearchFeedTypes.PRODUCTS:
          return this.$get(item, 'name', '');
        default:
          return '';
      }
    },
    getHandle(item) {
      switch (item.entity) {
        case SearchFeedTypes.CREATORS:
          return this.$get(item, 'profile.tagline', '');
        case SearchFeedTypes.RECIPES:
        case SearchFeedTypes.PRODUCTS:
          return '@' + this.$get(item, 'creator.profile.handle', '');
        default:
          return '';
      }
    },
    handleItemClick(item) {
      switch (item.entity) {
        case SearchFeedTypes.RECIPES:
          this.$router.push({ path: `/recipe/${item?._id}` });
          break;
        case SearchFeedTypes.PRODUCTS:
          this.$router.push({ path: `/products/${item?._id}` });
          break;
        case SearchFeedTypes.CREATORS:
          this.$router.push({
            path: `/creators/${this.$get(item, 'profile.handle', item?._id)}`
          });
          break;
        default:
      }
    },
    handleFilterClick(type) {
      this.$emit('reset-count');
      this.activeFilter = type;
    },
    handleShowMoreClick() {
      this.$emit('load-more', this.activeFilter);
    },
    isProductDigitalOnly(item) {
      if (item?.entity !== SearchFeedTypes.PRODUCTS) return false;
      return isItemHasOnlyDigitalFormat(item);
    }
  }
};
</script>

<style lang="scss" scoped>
.results {
  &__item {
    max-width: 173px;
    width: 160px;
    font-size: 14px;
    line-height: 18px;
    overflow: hidden;
    position: relative;

    &-title {
      font-family: 'Poppins-SemiBold', sans-serif;
    }

    &-digital-badge {
      position: absolute;
      width: 40px;
      right: 5px;
      top: 0;
    }
  }
}
</style>
